// OPTIONS SCANNER · search coverage, per chain, for either strategy: the requested ATM range, the strikes
// actually scanned (boundaries, below / above ATM), combinations evaluated, qualifying and kept rows, and
// whether the chain is COMPLETE or PARTIAL with the reason (range cap, token budget, rows per chain...).
import React, { useMemo, useState } from "react";
import { X } from "lucide-react";
import { usePoll } from "@/components/options/OptionsCommon";
import { CONTROL } from "@/components/options/ScannerTable";

const REASONS = {
  RANGE_CAPPED: "range above Max strikes / chain (scanned nearest ATM)",
  TOKEN_BUDGET: "WebSocket token budget full (raise Token budget or narrow the range)",
  ROWS_PER_CHAIN: "more qualifying rows than Rows / chain (dropped rows counted)",
  LISTED_STRIKES_EXHAUSTED: "the chain lists fewer strikes than requested (all listed strikes scanned)",
};
const n = (v) => (v === null || v === undefined ? "—" : Number(v).toLocaleString("en-IN"));

export function summarise(chains) {
  let complete = 0, partial = 0, combos = 0, qualifying = 0;
  const why = {};
  for (const c of chains || []) {
    const cv = c.coverage;
    if (!cv || c.status === "SETTLEMENT_EXCLUDED") continue;
    combos += cv.combinations || 0;
    qualifying += cv.qualifying || 0;
    if (cv.complete) complete += 1;
    else { partial += 1; (cv.reasons || []).forEach((r) => { why[r] = (why[r] || 0) + 1; }); }
  }
  return { complete, partial, combos, qualifying, why };
}

export default function CoveragePanel({ running, onClose }) {
  const res = usePoll(running ? "/ic/chains" : null, { ms: 5000 });
  const [only, setOnly] = useState("");
  const chains = useMemo(() => (res.data?.chains || []).filter((c) => !only
    || (only === "PARTIAL" ? c.coverage && !c.coverage.complete && c.status !== "SETTLEMENT_EXCLUDED" : c.status === only)), [res.data, only]);
  const sum = summarise(res.data?.chains);
  return (
    <div className="term-panel rounded-sm p-3 space-y-2 text-xs" data-testid="coverage-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <h3 className="font-bold">Search coverage</h3>
        <span className="mono" data-testid="coverage-summary">{sum.complete} chains complete · {sum.partial} partial
          · {n(sum.combos)} combinations evaluated · {n(sum.qualifying)} qualifying</span>
        {Object.entries(sum.why).map(([r, k]) => <span key={r} className="text-[#b38f00]" title={REASONS[r] || r}>{k} × {r}</span>)}
        <select className={`${CONTROL} ml-auto`} value={only} onChange={(e) => setOnly(e.target.value)} aria-label="Show chains">
          <option value="">All chains</option><option value="PARTIAL">Partial only</option><option value="OK">OK</option>
          <option value="OVER_TOKEN_BUDGET">Over token budget</option><option value="SETTLEMENT_EXCLUDED">Settlement excluded</option></select>
        {onClose && <button className="p-1 hover:bg-[var(--term-s3)] rounded-sm" onClick={onClose} aria-label="Close coverage"><X className="h-4 w-4" /></button>}
      </div>
      <div className="overflow-auto max-h-80">
        <table className="w-full mono text-[11px]" data-testid="coverage-table">
          <thead className="sticky top-0 bg-[var(--term-s3)] text-[var(--term-secondary)]"><tr>
            {["Underlying", "Expiry", "Status", "Spot", "ATM", "Scanned range", "Below ATM", "Above ATM", "Strikes",
              "Combinations", "Qualifying", "Kept", "Coverage"].map((h) => <th key={h} className="px-1.5 py-1 text-right first:text-left">{h}</th>)}
          </tr></thead>
          <tbody>{chains.map((c) => {
            const cv = c.coverage || {};
            const reasons = [...(cv.reasons || []), ...(cv.notes || [])];
            return (
              <tr key={c.key} className="border-t border-[var(--term-border)]" data-testid={`coverage-${c.key}`}>
                <td className="px-1.5 py-1 font-bold">{c.underlying} <span className="text-[9px]">{c.exchange}</span></td>
                <td className="px-1.5 text-right">{c.expiry}</td>
                <td className="px-1.5 text-right" title={c.reason || ""}>{c.status}</td>
                <td className="px-1.5 text-right">{n(c.spot)}</td>
                <td className="px-1.5 text-right">{n(cv.atm)}</td>
                <td className="px-1.5 text-right">{cv.low != null ? `${n(cv.low)} – ${n(cv.high)}` : "—"}
                  {cv.wing_extension && (cv.wing_extension.puts.length || cv.wing_extension.calls.length)
                    ? <span className="text-[#2563EB]" title="protective-wing extension beyond the range"> +wings</span> : null}</td>
                <td className="px-1.5 text-right">{cv.actual_below ?? "—"}{cv.requested_below != null ? ` / ${cv.requested_below}` : ""}</td>
                <td className="px-1.5 text-right">{cv.actual_above ?? "—"}{cv.requested_above != null ? ` / ${cv.requested_above}` : ""}</td>
                <td className="px-1.5 text-right">{cv.count ?? "—"}{cv.requested_count != null ? ` / ${cv.requested_count}` : ""}</td>
                <td className="px-1.5 text-right">{n(cv.combinations)}</td>
                <td className="px-1.5 text-right">{n(cv.qualifying)}</td>
                <td className="px-1.5 text-right">{n(cv.kept)}{cv.truncated ? <span className="text-[#b38f00]"> (−{n(cv.truncated)})</span> : null}</td>
                <td className={`px-1.5 text-right font-bold ${cv.complete ? "text-[#00C805]" : "text-[#b38f00]"}`}
                  title={reasons.map((r) => REASONS[r] || r).join("; ")}>
                  {c.status === "SETTLEMENT_EXCLUDED" ? "excluded" : cv.complete ? "COMPLETE" : "PARTIAL"}
                  {reasons.length ? <span className="font-normal"> {reasons.join(", ")}</span> : null}</td>
              </tr>);
          })}</tbody>
        </table>
        {!chains.length && <div className="p-3 text-[var(--term-muted)]">{running ? "No chains evaluated yet." : "Start the scanner to see coverage."}</div>}
      </div>
      <div className="text-[10px] text-[var(--term-muted)]">Below / Above / Strikes show scanned / requested. COMPLETE = every requested listed strike
        was subscribed and every qualifying row kept. Hover a PARTIAL cell for the reason.</div>
    </div>
  );
}
